import { OptionRow, SessionMeta, GammaResult } from '../../../../types';
import { Candle } from '../../../../types';

export function inferGamma(
  options: OptionRow[],
  candles: Candle[],
  meta: SessionMeta
): GammaResult {
  const curPrice = candles?.length ? candles[candles.length - 1].close : 0;
  const sorted = [...options].sort((a, b) => a.strike - b.strike);

  // Totals
  const totalCallOI = options.reduce((s, o) => s + o.callOI, 0);
  const totalPutOI  = options.reduce((s, o) => s + o.putOI,  0);
  const pcRatio = totalCallOI > 0 ? totalPutOI / totalCallOI : null;

  // Max pain: strike that minimizes total option value at expiration
  let minTotalPayout = Infinity;
  let maxPain = sorted[0]?.strike ?? null;
  for (const pivot of sorted) {
    let payout = 0;
    for (const o of sorted) {
      if (o.strike < pivot.strike) payout += o.callOI * (pivot.strike - o.strike);
      if (o.strike > pivot.strike) payout += o.putOI  * (o.strike - pivot.strike);
    }
    if (payout < minTotalPayout) { minTotalPayout = payout; maxPain = pivot.strike; }
  }

  // Call wall = strike with highest call OI
  const callWallStrike = sorted.reduce((best, o) => o.callOI > best.callOI ? o : best, sorted[0]);
  // Put wall
  const putWallStrike  = sorted.reduce((best, o) => o.putOI > best.putOI ? o : best, sorted[0]);
  // Pin (highest combined OI)
  const pinStrike = sorted.reduce((best, o) => (o.callOI + o.putOI) > (best.callOI + best.putOI) ? o : best, sorted[0]);

  // Gamma flip: strike where call OI ≈ put OI (closest balance point)
  let gammaFlip: number | null = null;
  let minBalance = Infinity;
  for (const o of sorted) {
    const total = o.callOI + o.putOI;
    if (!total) continue;
    const diff = Math.abs(o.callOI - o.putOI) / total;
    if (diff < minBalance) { minBalance = diff; gammaFlip = o.strike; }
  }

  // ATM strike
  const atmStrike = sorted.reduce((best, o) =>
    Math.abs(o.strike - curPrice) < Math.abs(best.strike - curPrice) ? o : best, sorted[0]);

  // Regime classification
  const aboveFlip = gammaFlip !== null && curPrice > gammaFlip;
  const nearFlip  = gammaFlip !== null && Math.abs(curPrice - gammaFlip) / curPrice < 0.012;

  let regime: GammaResult['regime'];
  let volRisk: GammaResult['volRisk'];
  if (nearFlip) {
    regime = 'Neutral / Transition';
    volRisk = 'Medium';
  } else if (aboveFlip) {
    regime = 'Long Gamma / Pinning';
    volRisk = 'Low';
  } else {
    regime = 'Short Gamma / Expansion';
    volRisk = 'High';
  }

  // Expected move (rough estimate using IV if available)
  let expectedMove: number | null = null;
  if (meta.iv && curPrice) {
    // 1-day expected move ≈ price × IV% × sqrt(1/252)
    expectedMove = curPrice * (meta.iv / 100) * Math.sqrt(1 / 252);
  }

  // Confidence based on data completeness
  const hasGamma = options.some(o => o.gamma !== null);
  const hasBothSides = totalCallOI > 0 && totalPutOI > 0;
  const confidence: GammaResult['confidence'] =
    hasGamma && hasBothSides ? 'High' : hasBothSides ? 'Medium' : 'Low';

  return {
    regime, volRisk, confidence, gammaFlip, maxPain,
    pinStrike: pinStrike?.strike ?? null,
    callWall: callWallStrike?.strike ?? null,
    putWall: putWallStrike?.strike ?? null,
    pcRatio, aboveFlip, nearFlip,
    expectedMove, atmStrike: atmStrike?.strike ?? null,
  };
}
